import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MessageService, MenuItem } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { Menu } from 'primeng/menu';
import { ProjectplanService } from '../../../services/projectplan.service';
import { ProjectPlanModel } from '../../../models/projectplan.model';
import { AuthService } from '../../../core/services/auth.service';
import { environment } from '../../../../environments/environment';
import WebViewer, { WebViewerInstance } from '@pdftron/webviewer';
import { firstValueFrom } from 'rxjs';
import { ProjectStateService } from '../../../services/project-state.service';
import { ProjectModel } from '../../../models/project.model';
import { buildProjectAssetDownloadFileName } from '../../../utils/download-file-name.utils';
import { MediaFallbackService } from '../../../services/media-fallback.service';

@Component({
  selector: 'app-plan-viewer',
  standalone: true,
  imports: [CommonModule, Toast, Menu],
  templateUrl: './plan-viewer.component.html',
  styleUrl: './plan-viewer.component.css'
})
export class PlanViewerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('viewerContainer', { static: false }) viewerContainer!: ElementRef;

  projectId: number = 0;
  planFolderId: number = 0;
  currentPlan: ProjectPlanModel | null = null;
  allPlansInFolder: ProjectPlanModel[] = [];
  currentPlanIndex: number = 0;
  currentProject: ProjectModel | null = null;

  isLoading: boolean = true;
  webViewerInstance: WebViewerInstance | null = null;
  private annotationSaveTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private annotationDirty = false;
  private isLoadingServerAnnotations = false;
  private readonly annotationSaveDelayMs = 1000;
  private activeDocumentObjectUrl: string | null = null;
  private currentAnnotationXfdf = '';

  // User information for annotation tracking
  private currentUserId: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectPlanService: ProjectplanService,
    private messageService: MessageService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private projectStateService: ProjectStateService,
    private mediaFallbackService: MediaFallbackService
  ) {}

  ngOnInit(): void {
    // Get current user information
    const currentUser = this.authService.getCurrentUser();
    if (currentUser) {
      this.currentUserId = currentUser.userId || null;
    } else {
      console.error('No user information available');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'User information not available. Please login again.'
      });
      this.goBackToPlans();
      return;
    }

    // Get route parameters
    const projectIdParam = this.route.snapshot.paramMap.get('projectId');
    const planFolderIdParam = this.route.snapshot.paramMap.get('planFolderId');

    if (!projectIdParam || !planFolderIdParam) {
      console.error('Missing route parameters');
      this.goBackToPlans();
      return;
    }

    this.projectId = parseInt(projectIdParam);
    this.currentProject = this.projectStateService.getCurrentProject();
    this.planFolderId = parseInt(planFolderIdParam);

    // Get navigation state data
    const navigation = this.router.getCurrentNavigation();
    const state = navigation?.extras.state || (window.history.state);

    if (!state || !state.projectPlanList) {
      console.error('No plan list provided in navigation state');
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Plan data not available. Please go back and try again.'
      });
      this.goBackToPlans();
      return;
    }

    // Set the plan list from navigation state
    this.allPlansInFolder = state.projectPlanList;

    // Find the selected plan or default to first plan
    if (state.selectedPlanId) {
      this.currentPlanIndex = this.allPlansInFolder.findIndex(p => p.planId === state.selectedPlanId);
      if (this.currentPlanIndex === -1) {
        this.currentPlanIndex = 0;
      }
    } else {
      this.currentPlanIndex = 0;
    }

    this.currentPlan = this.allPlansInFolder[this.currentPlanIndex];
  }

  ngAfterViewInit(): void {
    // Initialize WebViewer after the view has been initialized
    // This ensures the viewerContainer element is available
    this.initializeAndLoad();
  }

  private async initializeAndLoad(): Promise<void> {
    try {
      this.isLoading = true;
      await this.initializeWebViewer();
      await this.loadDocument();
    } catch (error) {
      console.error('Error initializing viewer:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to initialize viewer'
      });
      this.isLoading = false;
    }
  }

  private async initializeWebViewer(): Promise<void> {
    if (!this.viewerContainer?.nativeElement) {
      console.error('Viewer container not found');
      return;
    }

    try {
      this.webViewerInstance = await WebViewer.Iframe(
        {
          path: 'assets/webviewer/lib',  // Relative path - respects baseHref
          licenseKey: environment.apryseWebLicenseKey,
          enableAnnotations: true,
          enableMeasurement: true,
          enableRedaction: false,
        },
        this.viewerContainer.nativeElement
      );

      // Wait for WebViewer UI to be fully ready
      await new Promise<void>((resolve) => setTimeout(() => resolve(), 1000));

      // Enable annotation features
      this.webViewerInstance.UI.enableFeatures([
        this.webViewerInstance.UI.Feature.Annotations,
        this.webViewerInstance.UI.Feature.Measurement,
        this.webViewerInstance.UI.Feature.FilePicker,
        this.webViewerInstance.UI.Feature.NotesPanel,
        this.webViewerInstance.UI.Feature.Search,
        this.webViewerInstance.UI.Feature.TextSelection,
      ]);

      // Set up annotation event listeners for persistence and user tracking
      this.setupAnnotationListeners();
    } catch (error) {
      console.error('Error initializing WebViewer:', error);
      throw error;
    }
  }

  /**
   * Set up annotation event listeners to track user ownership
   * This prepares annotations for future backend persistence
   */
  private setupAnnotationListeners(): void {
    if (!this.webViewerInstance) return;

    const { Core } = this.webViewerInstance;
    const { annotationManager } = Core;

    annotationManager.addEventListener('annotationChanged', (annotations: any[], action: string, info: any) => {
      if (this.isLoadingServerAnnotations) {
        return;
      }

      if (info?.imported) {
        return;
      }

      if (action === 'add') {
        annotations.forEach((annot) => {
          // Attach user metadata to each annotation
          annot.setCustomData('userId', this.currentUserId?.toString() || '');
          annot.setCustomData('userName', `${this.authService.getCurrentUser()?.firstName || ''} ${this.authService.getCurrentUser()?.lastName || ''}`.trim());
          annot.setCustomData('createdAt', new Date().toISOString());
        });
      }

      this.annotationDirty = true;
      this.scheduleAnnotationSave();
    });
  }

  private async loadDocument(): Promise<void> {
    if (!this.webViewerInstance || !this.currentPlan) {
      return;
    }

    try {
      this.releaseActiveDocumentObjectUrl();
      let documentUrl = this.mediaFallbackService.buildCdnUrl(this.currentPlan.planPath);

      // Determine file extension
      const fileName = this.currentPlan.planFullName || this.currentPlan.planName || 'document.png';
      const extension = fileName.split('.').pop()?.toLowerCase() || 'png';

      // Load document into WebViewer directly from CDN
      const loadOptions: any = {
        filename: fileName,
      };

      if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff'].includes(extension)) {
        loadOptions.extension = extension;
        try {
          const image = await this.loadImageElement(documentUrl);
          loadOptions.pageSizes = [this.getLoadedImagePageSize(image)];
        } catch (error) {
          console.warn('CDN plan image failed before Apryse load, retrying through OCI fallback:', error);
          const fallbackUrl = await this.mediaFallbackService.getFallbackObjectUrl(this.currentPlan.planPath);
          if (!fallbackUrl) {
            throw error;
          }
          this.activeDocumentObjectUrl = fallbackUrl;
          documentUrl = fallbackUrl;

          const image = await this.loadImageElement(documentUrl);
          loadOptions.pageSizes = [this.getLoadedImagePageSize(image)];
        }
      }

      await this.loadServerAnnotationXfdf();
      try {
        await this.webViewerInstance.Core.documentViewer.loadDocument(documentUrl, loadOptions);
      } catch (error) {
        console.warn('CDN plan load failed, retrying through OCI fallback:', error);
        const fallbackUrl = await this.mediaFallbackService.getFallbackObjectUrl(this.currentPlan.planPath);
        if (!fallbackUrl) {
          throw error;
        }
        this.activeDocumentObjectUrl = fallbackUrl;
        await this.webViewerInstance.Core.documentViewer.loadDocument(fallbackUrl, loadOptions);
      }
      await this.importServerAnnotations();
      this.annotationDirty = false;
      this.isLoading = false;
      this.cdr.detectChanges();

    } catch (error) {
      console.error('Error loading document:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load document'
      });
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  goBackToPlans(): void {
    this.router.navigate(['/plans', this.projectId]);
  }

  async goToPreviousPlan(): Promise<void> {
    if (this.currentPlanIndex > 0) {
      await this.persistAnnotationsNow();
      this.currentPlanIndex--;
      this.currentPlan = this.allPlansInFolder[this.currentPlanIndex];
      await this.loadDocument();
    }
  }

  async goToNextPlan(): Promise<void> {
    if (this.currentPlanIndex < this.allPlansInFolder.length - 1) {
      await this.persistAnnotationsNow();
      this.currentPlanIndex++;
      this.currentPlan = this.allPlansInFolder[this.currentPlanIndex];
      await this.loadDocument();
    }
  }

  get hasPreviousPlan(): boolean {
    return this.currentPlanIndex > 0;
  }

  get hasNextPlan(): boolean {
    return this.currentPlanIndex < this.allPlansInFolder.length - 1;
  }

  get currentPageInfo(): string {
    if (!this.allPlansInFolder.length) return '';
    return `${this.currentPlanIndex + 1} of ${this.allPlansInFolder.length}`;
  }

  private readonly imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff'];

  get isCurrentPlanImage(): boolean {
    const ext = (this.currentPlan?.planFullName || this.currentPlan?.planName || '')
      .split('.').pop()?.toLowerCase() || '';
    return this.imageExtensions.includes(ext);
  }

  get downloadMenuItems(): MenuItem[] {
    return [
      { label: 'Original File', icon: 'pi pi-file', command: () => void this.downloadPlan() },
      { label: 'Optimized File', icon: 'pi pi-image', command: () => void this.downloadOptimizedPlan() }
    ];
  }

  async downloadPlan(): Promise<void> {
    if (!this.currentPlan?.planId) return;

    await this.persistAnnotationsNow();
    this.downloadPlanThroughApi(false);
  }

  async downloadOptimizedPlan(): Promise<void> {
    if (!this.currentPlan?.planId) return;

    await this.persistAnnotationsNow();
    this.downloadPlanThroughApi(true);
  }

  private async loadServerAnnotationXfdf(): Promise<void> {
    this.currentAnnotationXfdf = '';

    if (!this.webViewerInstance || !this.currentPlan?.planId) {
      return;
    }

    const annotation = await firstValueFrom(this.projectPlanService.getProjectPlanAnnotation(this.currentPlan.planId));
    this.currentAnnotationXfdf = annotation.annotationXfdf || '';
  }

  private async importServerAnnotations(): Promise<void> {
    if (!this.webViewerInstance || !this.currentAnnotationXfdf) {
      return;
    }

    this.isLoadingServerAnnotations = true;
    try {
      await this.webViewerInstance.Core.annotationManager.importAnnotations(this.currentAnnotationXfdf);
    } finally {
      this.isLoadingServerAnnotations = false;
    }
  }

  private scheduleAnnotationSave(): void {
    if (this.annotationSaveTimeoutId) {
      clearTimeout(this.annotationSaveTimeoutId);
    }

    this.annotationSaveTimeoutId = setTimeout(() => {
      void this.persistAnnotationsNow();
    }, this.annotationSaveDelayMs);
  }

  private async persistAnnotationsNow(): Promise<void> {
    if (!this.webViewerInstance || !this.currentPlan?.planId || !this.annotationDirty) {
      return;
    }

    if (this.annotationSaveTimeoutId) {
      clearTimeout(this.annotationSaveTimeoutId);
      this.annotationSaveTimeoutId = null;
    }

    const { annotationManager } = this.webViewerInstance.Core;
    const annotations = annotationManager.getAnnotationsList();
    const annotationXfdf = annotations.length > 0
      ? await annotationManager.exportAnnotations({
          generateInlineAppearances: true,
          links: false,
          widgets: false
        })
      : null;

    await firstValueFrom(this.projectPlanService.updateProjectPlanAnnotation(this.currentPlan.planId, annotationXfdf));
    this.annotationDirty = false;
  }

  private downloadPlanThroughApi(optimized: boolean): void {
    if (!this.currentPlan?.planId) {
      return;
    }

    this.projectPlanService.downloadProjectPlan(this.currentPlan.planId, optimized).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = buildProjectAssetDownloadFileName(
          this.currentProject,
          'Plan',
          this.currentPlan?.planFullName || this.currentPlan?.planName || 'plan',
          {
            variant: optimized ? 'Optimized' : 'Original',
            fallbackExtension: 'pdf',
          }
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      },
      error: (err) => {
        console.error('Error downloading plan:', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to download plan.' });
      }
    });
  }

  private async loadImageElement(imageUrl: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Unable to load image'));
      image.src = imageUrl;
    });
  }

  private async getImagePageSize(imageUrl: string): Promise<{ width: number; height: number } | null> {
    try {
      const image = await this.loadImageElement(imageUrl);
      const { width, height } = this.getLoadedImagePageSize(image);

      if (!width || !height) {
        return null;
      }

      return { width, height };
    } catch (error) {
      console.warn('Unable to determine plan image dimensions for WebViewer page sizing:', error);
      return null;
    }
  }

  private getLoadedImagePageSize(image: HTMLImageElement): { width: number; height: number } {
    return {
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height,
    };
  }

  ngOnDestroy(): void {
    void this.persistAnnotationsNow();
    this.releaseActiveDocumentObjectUrl();

    if (this.annotationSaveTimeoutId) {
      clearTimeout(this.annotationSaveTimeoutId);
      this.annotationSaveTimeoutId = null;
    }

    // Clean up WebViewer instance
    if (this.webViewerInstance) {
      try {
        this.webViewerInstance.UI.dispose();
        this.webViewerInstance = null;
      } catch (error) {
        console.error('Error disposing WebViewer:', error);
      }
    }
  }

  private releaseActiveDocumentObjectUrl(): void {
    if (this.activeDocumentObjectUrl) {
      this.mediaFallbackService.releaseObjectUrl(this.activeDocumentObjectUrl);
      this.activeDocumentObjectUrl = null;
    }
  }
}
